// frontend/src/screens/LoginScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { startAuthentication } from '@simplewebauthn/browser';
import axios from 'axios';
import { toast } from 'react-toastify';

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Fingerprint,
  LockReset,
  ArrowBack,
  MarkEmailRead,
} from '@mui/icons-material';

//import { useLoginMutation, usePResetMutation, useVerifyOtpMutation } from '../slices/usersApiSlice';

import { useLoginMutation, usePResetMutation, useVerifyOtpMutation,
         useVerifyPasswordResetOtpMutation } from '../slices/usersApiSlice';

import { setCredentials } from '../slices/authSlice';

// ─── OTP digit input component ────────────────────────────────────────────────
// Renders 6 individual boxes, auto-advances focus, handles paste.

const OTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const digits = value.split('');

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
      } else if (index > 0) {
        next[index - 1] = '';
        inputRefs.current[index - 1]?.focus();
      }
      onChange(next.join(''));
    }
  };

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1); // digits only
    const next = [...digits];
    next[index] = char;
    onChange(next.join(''));
    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    // Focus last filled or last box
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1.5 }, justifyContent: 'center', my: 2 }}>
      {Array.from({ length: 6 }, (_, i) => (
        <Box
          key={i}
          component="input"
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          sx={{
            width:  { xs: 40, sm: 52 },
            height: { xs: 48, sm: 60 },
            border: '2px solid',
            borderColor: digits[i] ? 'primary.main' : 'divider',
            borderRadius: 2,
            fontSize: { xs: '1.4rem', sm: '1.8rem' },
            fontWeight: 700,
            textAlign: 'center',
            fontFamily: 'monospace',
            bgcolor: digits[i] ? 'primary.50' : 'background.paper',
            color: 'primary.main',
            outline: 'none',
            transition: 'border-color 0.2s, background-color 0.2s',
            '&:focus': {
              borderColor: 'primary.dark',
              boxShadow: '0 0 0 3px rgba(25,118,210,0.15)',
            },
            '&:disabled': {
              bgcolor: '#f5f5f5',
              cursor: 'not-allowed',
            },
          }}
        />
      ))}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const STEP_CREDENTIALS = 'credentials';
const STEP_OTP         = 'otp';
const STEP_RESET_OTP   = 'reset_otp';     // ← new

const LoginScreen = () => {
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [step,            setStep]           = useState(STEP_CREDENTIALS);
  const [email,           setEmail]          = useState('');
  const [password,        setPassword]       = useState('');
  const [showPassword,    setShowPassword]   = useState(false);
  const [otpValue,        setOtpValue]       = useState('');
  const [otpEmail,        setOtpEmail]       = useState('');   // confirmed email from step 1
  const [resendCooldown,  setResendCooldown] = useState(0);


  const [resetEmail,        setResetEmail]       = useState('');
  const [resetOtpValue,     setResetOtpValue]     = useState('');
  const [verifyResetOtp, { isLoading: verifyingReset }] = useVerifyPasswordResetOtpMutation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [login,     { isLoading: loggingIn }]  = useLoginMutation();
  const [verifyOtp, { isLoading: verifying }]  = useVerifyOtpMutation();
  const [pReset,    { isLoading: resetting }]  = usePResetMutation();

  const { userInfo } = useSelector((state) => state.auth);
  const { search }   = useLocation();
  const redirect     = new URLSearchParams(search).get('redirect') || '/';

  useEffect(() => {
  if (step === STEP_RESET_OTP && resetOtpValue.length === 6) {
    handleResetOtpSubmit();
  }
}, [resetOtpValue, step]);



  useEffect(() => {
    if (userInfo) navigate(redirect);
  }, [navigate, redirect, userInfo]);

  // Cooldown ticker for "Resend code" button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // ── Step 1: submit email + password ────────────────────────────────────────
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await login({ email: email.trim().toLowerCase(), password }).unwrap();
      // Backend now returns { otpSent: true, email } instead of user data
      if (res.otpSent) {
        setOtpEmail(res.email);
        setOtpValue('');
        setStep(STEP_OTP);
        setResendCooldown(60);
        toast.info('A 6-digit code has been sent to your email.', { autoClose: 4000 });
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Login failed');
    }
  };

  // ── Step 2: submit OTP ──────────────────────────────────────────────────────
  const handleOtpSubmit = async (e) => {
    e?.preventDefault();
    if (otpValue.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }
    try {
      const res = await verifyOtp({ email: otpEmail, otp: otpValue }).unwrap();
      dispatch(setCredentials({ ...res }));
      navigate(redirect);
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Invalid or expired code');
      setOtpValue('');
    }
  };

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    if (step === STEP_OTP && otpValue.length === 6) {
      handleOtpSubmit();
    }
  }, [otpValue, step]);

  // ── Resend code ─────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await login({ email: otpEmail, password }).unwrap();
      setOtpValue('');
      setResendCooldown(60);
      toast.info('A new code has been sent to your email.');
    } catch (err) {
      toast.error('Could not resend code. Please go back and sign in again.');
    }
  };

  // ── Password reset ──────────────────────────────────────────────────────────
  const handlePasswordReset = async () => {
  if (!email) { toast.error('Enter your email first'); return; }
  try {
    await pReset({ email: email.trim().toLowerCase() }).unwrap();
    setResetEmail(email.trim().toLowerCase());
    setResetOtpValue('');
    setStep(STEP_RESET_OTP);
    setResendCooldown(60);
    toast.info('A reset code has been sent to your email.', { autoClose: 4000 });
  } catch (err) {
    toast.error(err?.data?.message || err?.error || 'Could not send reset code');
  }
};



const handleResendReset = async () => {
  if (resendCooldown > 0) return;
  try {
    await pReset({ email: resetEmail }).unwrap();
    setResetOtpValue('');
    setResendCooldown(60);
    toast.info('A new reset code has been sent.');
  } catch (err) {
    toast.error('Could not resend code. Please go back and try again.');
  }
};


//Add a handler for submitting the reset OTP:
const handleResetOtpSubmit = async (e) => {
  e?.preventDefault();
  if (resetOtpValue.length !== 6) {
    toast.error('Please enter the complete 6-digit code');
    return;
  }
  try {
    const res = await verifyResetOtp({ email: resetEmail, otp: resetOtpValue }).unwrap();
    // No sessionStorage — navigate with Router state (memory only, not persisted)
    navigate('/resetPassword', { state: { email: res.email } });
  } catch (err) {
    toast.error(err?.data?.message || err?.error || 'Invalid or expired code');
    setResetOtpValue('');
  }
};

const getCSRFToken = () =>
  document.cookie
    .split('; ')
    .find(row => row.startsWith('csrf-token='))
    ?.split('=')[1] ?? '';


  // ── WebAuthn (bypasses OTP — hardware key is already strong 2FA) ────────────
  const handleWebAuthnLogin = async () => {
  if (!email) { toast.error('Please enter your email first'); return; }
  try {
    const optionsRes = await axios.post(
      '/api/webauthn/generate-authentication',
      { email }
    );
    const asseResp = await startAuthentication(optionsRes.data);
    const verifyRes = await axios.post(
      '/api/webauthn/verify-authentication',
      { credential: asseResp, email },
      { headers: { 'X-CSRF-Token': getCSRFToken() } }  // ← add header
    );
    dispatch(setCredentials(verifyRes.data));
    navigate(redirect);
  } catch (err) {
    toast.error(err?.response?.data?.error || 'WebAuthn login failed');
  }
};

  // ── Layout shell ────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f0f4f8',
        p: 2,
      }}
    >
      <Paper
        elevation={isMobile ? 0 : 4}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {/* Header band */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          
              {(step === STEP_OTP || step === STEP_RESET_OTP) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setStep(STEP_CREDENTIALS);
                      setOtpValue('');
                      setResetOtpValue('');
                    }}
                    sx={{ color: 'white', mr: 0.5 }}
                  >
                    <ArrowBack />
                  </IconButton>
                )} 

          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              
              {step === STEP_CREDENTIALS ? 'Staff Portal Sign In'
                                            : step === STEP_OTP        ? 'Verify Your Identity'
                                            : 'Reset Your Password'}

            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Beaumont Hospital
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>

          {/* ── STEP 1: Credentials ── */}
          {step === STEP_CREDENTIALS && (
            <Box component="form" onSubmit={handleCredentialsSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? 'small' : 'medium'}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? 'small' : 'medium'}
              />

              <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loggingIn}
                  size={isMobile ? 'medium' : 'large'}
                  startIcon={loggingIn ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {loggingIn ? 'Checking…' : 'Continue'}
                </Button>

                <Button
                  variant="outlined"
                  disabled={resetting}
                  onClick={handlePasswordReset}
                  size={isMobile ? 'medium' : 'large'}
                  startIcon={<LockReset fontSize="small" />}
                  sx={{ flexShrink: 0 }}
                  title="Reset password"
                >
                  Reset
                </Button>
              </Box>

              <Divider sx={{ my: 2.5 }}>
                <Typography variant="caption" color="text.secondary">or</Typography>
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleWebAuthnLogin}
                startIcon={<Fingerprint />}
                size={isMobile ? 'medium' : 'large'}
                sx={{ mb: 2 }}
              >
                Sign in with YubiKey / Windows Hello
              </Button>

              <Typography variant="body2" color="text.secondary" align="center">
                No account?{' '}
                <Link
                  to={redirect ? `/register?redirect=${redirect}` : '/register'}
                  style={{ color: theme.palette.primary.main }}
                >
                  Register
                </Link>
              </Typography>
            </Box>
          )}

          {/* ── STEP 2: OTP entry ── */}
          {step === STEP_OTP && (
            <Box component="form" onSubmit={handleOtpSubmit}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <MarkEmailRead sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  We sent a 6-digit code to:
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  {otpEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Enter the code below. It expires in 5 minutes.
                </Typography>
              </Box>

              <OTPInput
                value={otpValue}
                onChange={setOtpValue}
                disabled={verifying}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={verifying || otpValue.length !== 6}
                size={isMobile ? 'medium' : 'large'}
                sx={{ mt: 1 }}
                startIcon={verifying ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {verifying ? 'Verifying…' : 'Verify & Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Didn't receive the code?{' '}
                  <Box
                    component="span"
                    onClick={resendCooldown > 0 ? undefined : handleResend}
                    sx={{
                      color: resendCooldown > 0 ? 'text.disabled' : 'primary.main',
                      cursor: resendCooldown > 0 ? 'default' : 'pointer',
                      fontWeight: 600,
                      textDecoration: resendCooldown === 0 ? 'underline' : 'none',
                    }}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </Box>
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mt: 2.5, fontSize: '0.78rem' }}>
                Check your spam/junk folder if you don't see the email within a minute.
              </Alert>
            </Box>
          )}

          {/* ── STEP 3: Password Reset OTP ── */}
  {step === STEP_RESET_OTP && (
    <Box component="form" onSubmit={handleResetOtpSubmit}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <LockReset sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="body1" gutterBottom>
          We sent a password reset code to:
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          {resetEmail}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Enter the 6-digit code below. It expires in 15 minutes.
        </Typography>
      </Box>

      <OTPInput
        value={resetOtpValue}
        onChange={setResetOtpValue}
        disabled={verifyingReset}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={verifyingReset || resetOtpValue.length !== 6}
        size={isMobile ? 'medium' : 'large'}
        sx={{ mt: 1 }}
        startIcon={verifyingReset ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {verifyingReset ? 'Verifying…' : 'Verify Code'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          Didn't receive the code?{' '}
          <Box
            component="span"
            onClick={resendCooldown > 0 ? undefined : handleResendReset}
            sx={{
              color: resendCooldown > 0 ? 'text.disabled' : 'primary.main',
              cursor: resendCooldown > 0 ? 'default' : 'pointer',
              fontWeight: 600,
              textDecoration: resendCooldown === 0 ? 'underline' : 'none',
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </Box>
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mt: 2.5, fontSize: '0.78rem' }}>
        Check your spam/junk folder if you don't see the email within a minute.
      </Alert>
    </Box>
  )}

        </Box>
      </Paper>
    </Box>
  );
};

export default LoginScreen;
